import { attr, customElement, FASTElement, observable } from '@microsoft/fast-element';
import { ProjectRfqsGraphStyles as styles } from './project-rfqs-graph.styles';
import { ProjectRfqsGraphTemplate as template } from './project-rfqs-graph.template';
import { IProjectService, IRfqService } from '../../../../services';
import { GRAPH_PERIODICITY } from '../../../../utils';
import { IRfq, Project } from '../../../../types';
import Highcharts from 'highcharts';
import { convertDateToUTC, getFormattedNumber, roundNumber } from '../../../../utils/formatting';
import {
  getLastDayAndTimeOfMonth,
  getLastMonthOfQuarterByDate,
  getQuarterOfTheYear,
} from '../../../../utils/chart-data-utils';

const name = 'project-rfqs-graph';

@customElement({
  name,
  template,
  styles,
})
export class ProjectRfqsGraph extends FASTElement {
  @IProjectService projectService: IProjectService;
  @IRfqService rfqService: IRfqService;

  @observable projectRfqChart!: HTMLDivElement;
  @observable periodicity: GRAPH_PERIODICITY = GRAPH_PERIODICITY.YEARLY;
  @observable forecastData: Array<Date>;
  @observable rfqsTimelineDate: Array<Date>;
  @observable public rfqList: Array<IRfq> = [];
  @observable weightAvgRfqPrice: number;
  @observable impliedCapacityFactor: number;
  @attr projectSelected: Project;

  private availableCapacity: number;
  private rfqVolume: number;
  private avgRfqPrice: number;
  private rfqDetailsArray: Array<any> = [];
  private avgPriceArray: Array<any> = [];

  connectedCallback() {
    super.connectedCallback();
  }

  public async projectSelectedChanged(oldValue: Project, newValue: Project) {
    if (newValue) {
      if (this.rfqList.length > 0) {
        this.rfqList = [];
      }
      this.getImpliedCapacityFactorValue(newValue.PROJECT_ID);
      let startRangeDate = new Date();
      let endRangeDate = new Date();

      this.availableCapacity = newValue.AVAILABLE_CAPACITY_AC;

      //call request reply for projectRangeDate
      await this.projectService.getProjectRangeDate(newValue?.PROJECT_ID).then((response) => {
        if (response && response.REPLY.length > 0) {
          startRangeDate = convertDateToUTC(new Date(response.REPLY[0].START_RANGE_DATE));
          endRangeDate = convertDateToUTC(new Date(response.REPLY[0].END_RANGE_DATE));
        }
      });

      this.forecastData = [startRangeDate, endRangeDate];

      this.renderProjectRfqsChart();
    }
  }

  private async getImpliedCapacityFactorValue(projectId) {
    await this.projectService.getProjectKeyMetrics(projectId).then((response) => {
      if (response && response.REPLY.length > 0) {
        this.impliedCapacityFactor = Number(response.REPLY[0]?.IMPLIED_CAPACITY_FACTOR?.toFixed(2));
      }
    });
  }

  public rfqsSelected(rfqList) {
    this.rfqDetailsArray = [];
    if (rfqList && rfqList.length > 0) {
      const hoursOnYear = 8760;
      this.rfqList = rfqList;
      rfqList.forEach((rfq, i) => {
        const objArray = {};
        this.rfqVolume = Number(
          getFormattedNumber(rfq.REQUESTED_VOLUME / (hoursOnYear * this.impliedCapacityFactor))
        );

        const date = new Date(rfq.TENOR_START_DATE);
        const startDate = convertDateToUTC(date);
        const endDate = convertDateToUTC(
          new Date(date.setMonth(date.getMonth() + rfq.TENOR_MONTHS))
        );
        endDate.setUTCDate(endDate.getUTCDate() - 1);
        if (startDate > this.forecastData[1]) {
          this.rfqList = rfqList.filter((el) => el.RFQ_ID !== rfq.RFQ_ID);
        } else {
          const rfqStartDate = startDate < this.forecastData[0] ? this.forecastData[0] : startDate;
          const rfqEndDate = endDate > this.forecastData[1] ? this.forecastData[1] : endDate;
          this.rfqsTimelineDate = [rfqStartDate, rfqEndDate];

          objArray[rfq.TICKER] = this.getValuesFromData(
            this.rfqsTimelineDate,
            this.rfqVolume,
            this.periodicity
          );

          this.avgRfqPrice = rfq.PRODUCT_TYPE == 'RECS' ? rfq.FIXED_PRICE : (rfq.FIXED_PRICE_LOWER_BOUND + rfq.FIXED_PRICE_UPPER_BOUND) / 2;
          objArray['timeline'] = this.rfqsTimelineDate;
          objArray['avgPrice'] = this.avgRfqPrice;
          objArray['ticker'] = rfq.TICKER;

          this.rfqDetailsArray.push(objArray);
        }
      });
      this.generateAvgRfqPrice(this.forecastData, this.rfqDetailsArray, this.periodicity);
      this.renderProjectRfqsChart();
    } else if (rfqList.length == 0 && this.projectSelected) {
      this.projectSelectedChanged(this.projectSelected, this.projectSelected);
    }
  }

  public generateAvgRfqPrice(projectTimeline, rfqsData, periodicity) {
    this.avgPriceArray = [];
    const forecastStartDate = convertDateToUTC(new Date(this.forecastData[0]));
    const forecastEndDate = convertDateToUTC(new Date(this.forecastData[1]));

    if (periodicity === GRAPH_PERIODICITY.YEARLY) {
      const yearsVals = [];

      for (
        let value = new Date(forecastStartDate).getFullYear(), i = 0;
        value <= forecastEndDate.getFullYear();
        value += 1, i++
      ) {
        rfqsData.forEach((rfq) => {
          if (yearsVals[i]) {
            if (yearsVals[i] > 0 && rfq[rfq.ticker][i] > 0) {
              yearsVals[i] = yearsVals[i].concat(rfq.avgPrice);
            } else if (rfq[rfq.ticker][i] > 0) {
              // if arr has a pre-existing 0, remove it
              if (yearsVals[i].includes(0)) {
                yearsVals[i] = yearsVals[i].filter((el) => el !== 0);
              }
              yearsVals[i] = yearsVals[i].concat(rfq.avgPrice);
            }
          } else if (!yearsVals[i]) {
            if (rfq[rfq.ticker][i] > 0) {
              yearsVals[i] = [rfq.avgPrice];
            } else {
              yearsVals[i] = [0];
            }
          }
        });
      }

      yearsVals.forEach((year) => {
        if (year.length) {
          const average = year.reduce((a, b) => a + b) / year.length;
          this.avgPriceArray.push(roundNumber(average));
        }
      });
    }

    if (periodicity === GRAPH_PERIODICITY.QUATERLY) {
      const quartersVals = [];

      forecastEndDate.setMonth(getLastMonthOfQuarterByDate(forecastEndDate));
      const forecastEndQuarterDate = getLastDayAndTimeOfMonth(forecastEndDate);
      for (
        let value = new Date(forecastStartDate), i = 0;
        value.valueOf() <= forecastEndQuarterDate.valueOf();
        value.setMonth(new Date(value).getMonth() + 3), i++
      ) {
        rfqsData.forEach((rfq) => {
          if (quartersVals[i]) {
            if (quartersVals[i] > 0 && rfq[rfq.ticker][i] > 0) {
              quartersVals[i] = quartersVals[i].concat(rfq.avgPrice);
            } else if (rfq[rfq.ticker][i] > 0) {
              // if arr has a pre-existing 0, remove it
              if (quartersVals[i].includes(0)) {
                quartersVals[i] = quartersVals[i].filter((el) => el !== 0);
              }
              quartersVals[i] = quartersVals[i].concat(rfq.avgPrice);
            }
          } else if (!quartersVals[i]) {
            if (rfq[rfq.ticker][i] > 0) {
              quartersVals[i] = [rfq.avgPrice];
            } else {
              quartersVals[i] = [0];
            }
          }
        });
      }

      quartersVals.forEach((quarter) => {
        if (quarter.length) {
          const average = quarter.reduce((a, b) => a + b) / quarter.length;
          this.avgPriceArray.push(roundNumber(average));
        }
      });
    }
  }

  private getValuesFromData(
    date: Array<any>,
    valueAttr,
    periodicity: GRAPH_PERIODICITY
  ): Array<any> {
    const rfqStartDate = convertDateToUTC(new Date(date[0]));
    const rfqEndDate = convertDateToUTC(new Date(date[1]));
    const forecastStartDate = convertDateToUTC(new Date(this.forecastData[0]));
    const forecastEndDate = convertDateToUTC(new Date(this.forecastData[1]));

    if (periodicity === GRAPH_PERIODICITY.YEARLY) {
      const values = [];
      // if RFQs start after/ end before project timeline, add 0
      let val = forecastStartDate.getFullYear();
      while (val < rfqStartDate.getFullYear()) {
        values.push(0);
        val += 1;
      }

      while (val <= rfqEndDate.getFullYear()) {
        values.push(valueAttr);
        val += 1;
      }

      while (val < forecastEndDate.getFullYear()) {
        values.push(0);
        val += 1;
      }

      return values;
    }

    if (periodicity === GRAPH_PERIODICITY.QUATERLY) {
      const quarterlyArray = [];

      //Get end of first quarter
      const lastMonthOfQuarter = new Date(forecastStartDate);
      lastMonthOfQuarter.setDate(1);
      lastMonthOfQuarter.setMonth(getLastMonthOfQuarterByDate(lastMonthOfQuarter));
      //const endOfQuarter = getLastDayAndTimeOfMonth(lastMonthofQuarter);

      forecastEndDate.setMonth(getLastMonthOfQuarterByDate(forecastEndDate));
      const forecastEndQuarterDate = getLastDayAndTimeOfMonth(forecastEndDate);

      while (getLastDayAndTimeOfMonth(lastMonthOfQuarter) < rfqStartDate) {
        quarterlyArray.push(0);
        lastMonthOfQuarter.setMonth(lastMonthOfQuarter.getMonth() + 3);
      }

      while (getLastDayAndTimeOfMonth(lastMonthOfQuarter) <= rfqEndDate) {
        quarterlyArray.push(valueAttr);
        lastMonthOfQuarter.setMonth(lastMonthOfQuarter.getMonth() + 3);
        lastMonthOfQuarter.setDate(1);
      }

      quarterlyArray.push(valueAttr);

      while (getLastDayAndTimeOfMonth(lastMonthOfQuarter) <= forecastEndQuarterDate) {
        quarterlyArray.push(0);
        lastMonthOfQuarter.setMonth(lastMonthOfQuarter.getMonth() + 3);
        lastMonthOfQuarter.setDate(1);
      }

      quarterlyArray.pop();

      return quarterlyArray;
    }
  }

  private getProjectValues(
    date: Array<any>,
    valueAttr,
    periodicity: GRAPH_PERIODICITY
  ): Array<any> {
    const startDate = convertDateToUTC(new Date(date[0]));
    const endDate = convertDateToUTC(new Date(date[1]));

    if (periodicity === GRAPH_PERIODICITY.YEARLY) {
      const values = [];

      for (let value = startDate.getFullYear(); value <= endDate.getFullYear(); value += 1) {
        values.push(valueAttr);
      }

      return values;
    }

    if (periodicity === GRAPH_PERIODICITY.QUATERLY) {
      endDate.setMonth(getLastMonthOfQuarterByDate(endDate));
      const forecastEndQuarterDate = getLastDayAndTimeOfMonth(endDate);

      const start = this.setStartDate(startDate);
      const quaterlyArray = [];

      for (
        let value = new Date(start);
        value.valueOf() < forecastEndQuarterDate.valueOf();
        value.setMonth(value.getMonth() + 3)
      ) {
        quaterlyArray.push(valueAttr);
      }

      return quaterlyArray;
    }
  }

  private getDatesFromData(data: Array<any>, periodicity: GRAPH_PERIODICITY) {
    if (periodicity === GRAPH_PERIODICITY.YEARLY) {
      const startDate = data[0].getFullYear();
      const endDate = data[1].getFullYear();
      const years = [];

      for (let value = startDate; value <= endDate; value += 1) years.push(value.toString());
      return years;
    }

    if (periodicity === GRAPH_PERIODICITY.QUATERLY) {
      const startDate = new Date(data[0]);
      const start = this.setStartDate(startDate);
      const endDate = new Date(data[1]);
      const end = this.setEndDate(endDate);
      const quaterlyArray = [];

      for (
        let value = new Date(start);
        value.valueOf() <= end.valueOf();
        value.setMonth(new Date(value).getMonth() + 3)
      ) {
        const quarterNum = Math.floor((value.getMonth() + 3) / 3);
        const currQuarter = value.getFullYear() + ' Q' + quarterNum;
        quaterlyArray.push(currQuarter);
      }

      return quaterlyArray;
    }
  }

  private setStartDate(date) {
    const quarterNum = getQuarterOfTheYear(new Date(date));
    let finalMonth = 0;
    switch (quarterNum) {
      case 1:
        finalMonth = 0;
        break;
      case 2:
        finalMonth = 3;
        break;
      case 3:
        finalMonth = 6;
        break;
      case 4:
        finalMonth = 9;
        break;
      default:
        break;
    }
    const finalDate = new Date(date.setMonth(finalMonth));
    return finalDate;
  }

  private setEndDate(date) {
    const quarterNum = getQuarterOfTheYear(new Date(date));
    let finalMonth = 0;
    switch (quarterNum) {
      case 1:
        finalMonth = 2;
        break;
      case 2:
        finalMonth = 5;
        break;
      case 3:
        finalMonth = 8;
        break;
      case 4:
        finalMonth = 11;
        break;
      default:
        break;
    }
    const finalDate = new Date(date.setMonth(finalMonth));
    return finalDate;
  }

  private getDataArrays() {
    const dataArrays: any[] = [];
    if (this.projectSelected && this.rfqDetailsArray.length) {
      dataArrays.push({
        name: 'Capacity MW',
        type: 'area',
        yAxis: 1, // project and rfqs use same y-axis to display values
        color: Highcharts.getOptions().colors[4],
        data: this.getProjectValues(this.forecastData, this.availableCapacity, this.periodicity),
        marker: {
          enabled: false,
        },
        dashStyle: 'ShortDot',
        tooltip: {
          valueSuffix: ' MW',
        },
      });
      this.rfqDetailsArray.forEach((rfq, i) => {
        dataArrays.push({
          type: 'column',
          yAxis: 1,
          color: Highcharts.getOptions().colors[i],
          name: Object.keys(rfq)[0],
          data: rfq[Object.keys(rfq)[0]],
        });
      });
      dataArrays.push({
        name: 'Weighted Avg RFQ Price',
        type: 'line',
        lineWidth: 4,
        color: Highcharts.getOptions().colors[7],
        yAxis: 2,
        data: this.avgPriceArray,
        zIndex: 1,
        marker: {
          enabled: false,
        },
        tooltip: {
          valuePrefix: '$',
        },
      });
    } else if (this.projectSelected && this.rfqList.length === 0) {
      dataArrays.push({
        name: 'Capacity MW',
        type: 'area',
        yAxis: 1,
        color: Highcharts.getOptions().colors[4],
        data: this.getProjectValues(this.forecastData, this.availableCapacity, this.periodicity),
        marker: {
          enabled: false,
        },
        dashStyle: 'ShortDot',
        tooltip: {
          valueSuffix: ' MW',
        },
      });
    }

    return dataArrays;
  }

  private renderProjectRfqsChart() {
    const dates = this.getDatesFromData(this.forecastData, this.periodicity);

    const dataArrays = this.getDataArrays();

    const chart = Highcharts.chart(
      {
        chart: {
          renderTo: this.projectRfqChart,
        },
        title: { text: '' },
        xAxis: {
          categories: dates,
        },
        yAxis: [
          {
            // Primary Y-Axis :
            title: {
              text: '', // "Capacity MW",
            },
            labels: {
              enabled: false,
            },
          },
          {
            // Secondary Y-Axis :
            title: {
              text: 'Capacity (MW)',
            },
          },
          {
            // Tertiary Y-Axis :
            title: {
              text: 'Price ($/MWh)',
              rotation: -90,
              margin: 30,
            },
            opposite: true,
          },
        ],
        tooltip: {
          shared: true,
        },
        plotOptions: {
          line:{step:'left'},
          column: {
            stacking: 'normal',
          },
        },
        series: dataArrays,
      },
      function (chart) {
        // to display capacity MW for project
        const series = chart.series;
        series.forEach((series) => {
          if (series.type == 'area') {
            const x = chart['plotSizeX'] / 2;
            const y = chart.plotTop + 20;
            const name = 'Capacity MW';
            chart.renderer
              .text(name, x, y)
              .css({
                color: '#fff',
                fontSize: '16px',
              })
              .add()
              .toFront();
          }
        });
      }
    );
  }

  public periodicityChanged(oldValue, newValue) {
    if (this.projectSelected && this.rfqDetailsArray.length) {
      this.rfqsSelected(this.rfqList);
    } else if (this.projectSelected) {
      this.renderProjectRfqsChart();
    }
  }

  public reflow() {
    if (this.projectSelected) {
      setTimeout(() => {
        this.renderProjectRfqsChart();
      }, 1);
    }
  }
}
